/**
 * Minimal, dependency-free SSE streaming client built on native fetch +
 * ReadableStream. Replaces @microsoft/fetch-event-source (unmaintained since
 * 2021) whose hidden retry and visibilitychange semantics caused silent
 * request restarts mid-generation.
 *
 * Contract:
 * - No automatic retries: any transport failure throws to the caller, which
 *   decides on error handling. A blind re-POST of a chat completion would
 *   re-generate (and re-bill) the answer with stale auth.
 * - No visibility-related aborts: the stream keeps flowing while the tab/PWA
 *   is hidden.
 * - Parses the SSE wire format per spec: CRLF/LF/CR line endings, comments
 *   (keepalives), multi-line data, default event name ''. id/retry fields
 *   are intentionally ignored.
 */
export interface SseStreamMessageInterface {
    event: string;
    data: string;
}

export interface SseStreamOptionsInterface {
    method?: string;
    headers: Record<string, string>;
    body?: string;
    signal?: AbortSignal;
    onOpen?: (response: Response) => Promise<void> | void;
    onMessage: (message: SseStreamMessageInterface) => Promise<void> | void;
    onClose?: () => void;
}

export class StreamUtil {

    public static readonly EventStreamContentType = 'text/event-stream';

    /**
     * Performs a POST request and dispatches the `text/event-stream` body as
     * parsed messages. Resolves once the stream ends; throws on any transport
     * or onOpen error. onClose is only invoked after a regular stream end.
     *
     * @param url request url
     * @param options headers, body, abort signal and stream callbacks
     */
    public static async postEventStream(url: string, options: SseStreamOptionsInterface): Promise<void> {
        const response = await fetch(url, {
            method: options.method ?? 'POST',
            headers: options.headers,
            body: options.body,
            signal: options.signal,
        });

        if (options.onOpen) {
            try {
                await options.onOpen(response);
            } catch (error) {
                // Release the (unread) connection so it is not kept open.
                try {
                    await response.body?.cancel();
                } catch {
                    // ignore — the connection is being torn down anyway
                }
                throw error;
            }
        }

        if (!response.body) {
            throw new Error('SSE response has no body');
        }

        await StreamUtil.readSseStream(response.body, options.onMessage);
        options.onClose?.();
    }

    /**
     * Reads a ReadableStream of UTF-8 bytes and dispatches SSE messages.
     * Handles events split across chunk boundaries via a line buffer.
     *
     * @param body response byte stream
     * @param onMessage callback for every dispatched event
     */
    public static async readSseStream(
        body: ReadableStream<Uint8Array>,
        onMessage: (message: SseStreamMessageInterface) => Promise<void> | void,
    ): Promise<void> {
        const reader = body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let eventName = '';
        let dataLines: string[] = [];

        // Per SSE spec an event without data is never dispatched.
        const dispatch = () => {
            if (dataLines.length > 0) {
                onMessage({ event: eventName, data: dataLines.join('\n') });
            }
            eventName = '';
            dataLines = [];
        };

        const processLine = (line: string) => {
            if (line === '') {
                dispatch();
                return;
            }
            if (line.startsWith(':')) {
                // Comment / keepalive — ignore
                return;
            }
            const colonIndex = line.indexOf(':');
            const field = colonIndex === -1 ? line : line.slice(0, colonIndex);
            let value = colonIndex === -1 ? '' : line.slice(colonIndex + 1);
            if (value.startsWith(' ')) {
                value = value.slice(1);
            }
            if (field === 'event') {
                eventName = value;
            } else if (field === 'data') {
                dataLines.push(value);
            }
            // id / retry fields intentionally ignored
        };

        // Consumes all complete lines currently buffered; on flush the
        // trailing line (without a line terminator) is processed too.
        const processBuffer = (flush: boolean) => {
            let match: RegExpExecArray | null;
            const lineBreak = /\r\n|\r|\n/;
            while ((match = lineBreak.exec(buffer)) !== null) {
                const line = buffer.slice(0, match.index);
                buffer = buffer.slice(match.index + match[0].length);
                processLine(line);
            }
            if (flush && buffer !== '') {
                processLine(buffer);
                buffer = '';
            }
        };

        try {
            for (;;) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }
                buffer += decoder.decode(value, { stream: true });
                processBuffer(false);
            }
            buffer += decoder.decode();
            processBuffer(true);
            dispatch();
        } finally {
            reader.releaseLock();
        }
    }
}
